import React from "react";
import { Link } from "react-router-dom";
import { Sparkles, Flame, Stamp, ArrowRight, Check, Landmark, PawPrint, ScrollText } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { countryOfDay, dayNumber, challengeNumber, squares } from "@/lib/daily";
import { readDaily, liveStreak, streakLabel } from "@/lib/engagement";
import { usePassport, nextBadge } from "@/lib/usePassport";
import { trackEvent } from "@/lib/analytics";
import PushToggle from "@/components/PushToggle";
import DishOfDay from "@/components/DishOfDay";
import CountryWheel from "@/components/CountryWheel";

const CARD = "rounded-2xl border border-stoneLine bg-card p-4 flex flex-col gap-2 min-w-0";
const KICKER = "flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-muted-foreground";

export function CountryOfDay({ country, onSelect }) {
  const { lang, t } = useLang();
  if (!country) return null;
  const facts = [
    { Icon: Landmark, label: t("capital"), value: country.capital?.[lang] },
    { Icon: PawPrint, label: t("fauna"), value: country.nature?.fauna?.[lang] },
    { Icon: ScrollText, label: t("history"), value: country.historical_note?.[lang] },
  ].filter((f) => f.value);
  return (
    <div className={`${CARD} col-span-2 md:row-span-2 relative overflow-hidden`} data-testid="country-of-day-card">
      <button onClick={() => { onSelect(country.code); trackEvent("country_of_day_open", { country_code: country.code }); }} data-testid="country-of-day"
        className="text-start flex flex-col gap-2 min-w-0 group -m-4 p-4 mb-0 rounded-t-2xl hover:bg-sand/40 transition-colors relative">
        <span className="absolute -end-6 -top-6 w-28 h-28 rounded-full bg-ochre/10 group-hover:bg-ochre/20 transition-colors" />
        <span className={KICKER}><Sparkles className="w-3 h-3 text-ochre" /> {t("country_of_day")}</span>
        <span className="flex items-center gap-3 w-full">
          <img src={country.flag_url} alt="" className="w-14 h-10 object-cover rounded-md border border-stoneLine shadow-sm" />
          <span className="min-w-0">
            <span className="block font-display font-bold text-xl text-ink leading-tight truncate" data-testid="country-of-day-name">{country.short_names[lang]}</span>
            <span className="block text-xs text-muted-foreground truncate">{country.names[lang]}</span>
          </span>
          <ArrowRight className="w-5 h-5 ms-auto shrink-0 text-muted-foreground group-hover:text-ochre rtl:rotate-180 transition-colors" />
        </span>
        <ul className="mt-1 grid gap-1.5 text-sm w-full">
          {facts.map(({ Icon, label, value }) => (
            <li key={label} className="flex gap-2 min-w-0"><Icon className="w-4 h-4 mt-0.5 shrink-0 text-ochre" /><span className="min-w-0 line-clamp-2"><span className="text-muted-foreground">{label}: </span><span className="text-ink">{value}</span></span></li>
          ))}
        </ul>
      </button>
      <DishOfDay country={country} />
    </div>
  );
}

export function ChallengeCard() {
  const { t, lang } = useLang();
  const day = dayNumber();
  const result = readDaily(day);
  const streak = liveStreak();
  return (
    <Link to={`/quiz?daily=1&lang=${lang}`} data-testid="daily-challenge-card" className={`${CARD} hover:border-ochre transition-colors group`}>
      <span className={KICKER}><Flame className={`w-3 h-3 ${streak ? "text-ochre" : ""}`} /> {t("daily_challenge")} #{challengeNumber(day)}</span>
      {result ? (
        <>
          <span className="font-display font-bold text-xl text-ink flex items-center gap-2"><Check className="w-5 h-5 text-oasis" /> {result.score}/5</span>
          <span className="text-lg tracking-wider" data-testid="daily-squares">{squares(result.picks)}</span>
          <span className="text-xs text-muted-foreground">{t("daily_done_hint")}</span>
        </>
      ) : (
        <>
          <span className="font-display font-bold text-xl text-ink">{t("daily_cta")}</span>
          <span className="text-xs text-muted-foreground">{t("daily_sub")}</span>
        </>
      )}
      <span className="mt-auto flex items-center justify-between gap-2 flex-wrap">
        <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-ochre" data-testid="daily-streak">
          <Flame className="w-3.5 h-3.5" fill={streak ? "currentColor" : "none"} /> {streakLabel(t, streak)}
        </span>
        <PushToggle compact />
      </span>
    </Link>
  );
}

export function PassportCard({ total }) {
  const { t, lang } = useLang();
  const { count } = usePassport();
  const next = nextBadge(count);
  const pct = total ? Math.round((count / total) * 100) : 0;
  return (
    <Link to={`/passport?lang=${lang}`} data-testid="passport-card" className={`${CARD} hover:border-ochre transition-colors`}>
      <span className={KICKER}><Stamp className="w-3 h-3 text-ochre" /> {t("passport")}</span>
      <span className="font-display font-bold text-xl text-ink" data-testid="passport-count">{count}/{total}</span>
      <span className="h-2 rounded-full bg-sandDeep overflow-hidden"><span className="block h-full bg-ochre transition-[width] duration-700" style={{ width: `${pct}%` }} /></span>
      <span className="text-xs text-muted-foreground mt-auto">
        {next ? t("passport_next").replace("{n}", next.min - count).replace("{badge}", t(`badge_${next.id}`)) : t("passport_complete")}
      </span>
    </Link>
  );
}

/** Home "come back every day" bento: country of the day (tall) · challenge · passport · country wheel (wide). */
export default function DailyStrip({ countries, onSelect }) {
  return (
    <div className="mb-4 sm:mb-8 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="daily-strip">
      <CountryOfDay country={countryOfDay(countries)} onSelect={onSelect} />
      <ChallengeCard />
      <PassportCard total={countries.length} />
      <div className="col-span-2"><CountryWheel countries={countries} onSelect={onSelect} /></div>
    </div>
  );
}
